/**
 * Calendario — widget de calendário mensal com marcação de lembretes
 * (port do calendarWidget() do dashboard.html). Lê /api/v1/lembretes do
 * mês exibido; clicar num dia abre o formulário de novo lembrete.
 */

import {
  faCalendarAlt,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { criarLembrete, listarLembretes } from '../../../api/tarefas'
import { useToast } from '../../../components/Toast'
import { CardWidget } from './CardWidget'

const DIAS_SEMANA = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S']

function ehHoje(ano: number, mes: number, dia: number) {
  const hoje = new Date()
  return (
    hoje.getFullYear() === ano && hoje.getMonth() + 1 === mes && hoje.getDate() === dia
  )
}

export function Calendario() {
  const hoje = new Date()
  const [ano, setAno] = useState(hoje.getFullYear())
  const [mes, setMes] = useState(hoje.getMonth() + 1) // 1-12
  const [diaSelecionado, setDiaSelecionado] = useState<number | null>(null)
  const [titulo, setTitulo] = useState('')
  const { notificar } = useToast()
  const clienteQuery = useQueryClient()

  const consulta = useQuery({
    queryKey: ['lembretes-mes', mes, ano],
    queryFn: () => listarLembretes({ mes, ano }),
  })

  const criar = useMutation({
    mutationFn: () =>
      criarLembrete({
        titulo: titulo.trim(),
        data: `${ano}-${String(mes).padStart(2, '0')}-${String(diaSelecionado).padStart(2, '0')}`,
      }),
    onSuccess: () => {
      setTitulo('')
      setDiaSelecionado(null)
      void clienteQuery.invalidateQueries({ queryKey: ['lembretes-mes'] })
      void clienteQuery.invalidateQueries({ queryKey: ['lembretes'] })
      notificar('success', 'Lembrete criado com sucesso!')
    },
    onError: () => notificar('error', 'Erro ao criar o lembrete.'),
  })

  const { diasDoMes, diasEmBranco, porDia } = useMemo(() => {
    const total = new Date(ano, mes, 0).getDate()
    const primeiroDiaSemana = new Date(ano, mes - 1, 1).getDay()
    const contagem = new Map<number, number>()
    for (const l of consulta.data?.lembretes ?? []) {
      const dia = Number(l.data_lembrete.slice(8, 10))
      contagem.set(dia, (contagem.get(dia) ?? 0) + 1)
    }
    return {
      diasDoMes: Array.from({ length: total }, (_, i) => i + 1),
      diasEmBranco: Array.from({ length: primeiroDiaSemana }, (_, i) => i),
      porDia: contagem,
    }
  }, [ano, mes, consulta.data])

  const nomeDoMes = new Date(ano, mes - 1, 1).toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
  })

  function navegar(delta: number) {
    const novo = new Date(ano, mes - 1 + delta, 1)
    setAno(novo.getFullYear())
    setMes(novo.getMonth() + 1)
    setDiaSelecionado(null)
  }

  return (
    <CardWidget
      titulo="Calendário"
      subtitulo={nomeDoMes}
      icone={faCalendarAlt}
      gradiente="from-pink-600 to-rose-600"
      corSubtitulo="text-pink-100"
      acoes={
        <div className="flex gap-1">
          <button
            onClick={() => navegar(-1)}
            aria-label="Mês anterior"
            className="flex h-8 w-8 items-center justify-center rounded-lg text-white transition-colors hover:bg-white/20"
          >
            <FontAwesomeIcon icon={faChevronLeft} className="text-xs" />
          </button>
          <button
            onClick={() => navegar(1)}
            aria-label="Próximo mês"
            className="flex h-8 w-8 items-center justify-center rounded-lg text-white transition-colors hover:bg-white/20"
          >
            <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
          </button>
        </div>
      }
    >
      <div className="p-4">
        <div className="mb-2 grid grid-cols-7 gap-1 text-center">
          {DIAS_SEMANA.map((d, i) => (
            <span key={i} className="text-[10px] font-bold uppercase text-slate-400">
              {d}
            </span>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {diasEmBranco.map((b) => (
            <div key={`b${b}`} className="aspect-square" />
          ))}
          {diasDoMes.map((dia) => {
            const temLembrete = porDia.has(dia)
            const hojeMarcado = ehHoje(ano, mes, dia)
            const selecionado = diaSelecionado === dia
            return (
              <button
                key={dia}
                onClick={() => setDiaSelecionado(selecionado ? null : dia)}
                title={temLembrete ? `${porDia.get(dia)} lembrete(s)` : undefined}
                className={`relative flex aspect-square items-center justify-center rounded-lg text-sm transition-all ${
                  hojeMarcado
                    ? 'bg-gradient-to-br from-pink-600 to-rose-600 font-bold text-white shadow-lg'
                    : selecionado
                      ? 'bg-pink-100 text-pink-700 ring-2 ring-pink-400 dark:bg-pink-900/40'
                      : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-dark-hover'
                }`}
              >
                <span>{dia}</span>
                {temLembrete && (
                  <span className="absolute bottom-0.5 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-amber-500" />
                )}
              </button>
            )
          })}
        </div>

        {diaSelecionado !== null && (
          <form
            onSubmit={(e) => {
              e.preventDefault()
              if (titulo.trim()) criar.mutate()
            }}
            className="mt-4 rounded-xl border border-pink-100 bg-pink-50 p-3 dark:border-pink-900/40 dark:bg-pink-900/10"
          >
            <p className="mb-2 text-xs font-bold uppercase text-pink-500">
              Novo lembrete — dia {diaSelecionado}
            </p>
            <div className="flex gap-2">
              <input
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                placeholder="O que você precisa lembrar?"
                className="min-w-0 flex-1 rounded-md border border-pink-200 px-2 py-1.5 text-sm dark:border-dark-border dark:bg-dark-bg dark:text-gray-100"
              />
              <button
                type="submit"
                disabled={criar.isPending || !titulo.trim()}
                className="rounded-md bg-pink-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-pink-700 disabled:opacity-50"
              >
                Criar
              </button>
            </div>
          </form>
        )}

        <div className="mt-4 border-t border-slate-100 pt-4 dark:border-slate-700">
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-2 text-slate-500">
              <span className="h-2 w-2 rounded-full bg-pink-600" />
              <span>
                Hoje:{' '}
                {hoje.toLocaleDateString('pt-BR', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
              </span>
            </div>
            <div className="flex items-center gap-2 text-amber-600">
              <span className="h-2 w-2 rounded-full bg-amber-500" />
              <span>{consulta.data?.lembretes.length ?? 0} lembrete(s)</span>
            </div>
          </div>
        </div>
      </div>
    </CardWidget>
  )
}
